import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { Provider } from 'react-redux';
import { store } from './store';
import { theme } from './styles/theme';
import { NotificationProvider } from './contexts/NotificationContext';
import MainLayout from './components/layout/MainLayout';
import SMSSettings from './pages/settings/SMSSettings';
import NotificationSettings from './pages/settings/NotificationSettings';
import { authService } from './services/auth.service';
import { getLoginUrlWithRedirect } from './constants/training';

// Lazy load pages
const Login = React.lazy(() => import('./pages/auth/Login'));
const Register = React.lazy(() => import('./pages/auth/Register'));
const ResetPassword = React.lazy(() => import('./pages/ResetPassword'));
const RequestPasswordReset = React.lazy(() => import('./pages/RequestPasswordReset'));
const Dashboard = React.lazy(() => import('./pages/dashboard/Dashboard'));
const Orders = React.lazy(() => import('./pages/orders/Orders'));
const NewOrder = React.lazy(() => import('./pages/orders/NewOrder'));
const Customers = React.lazy(() => import('./pages/customers/Customers'));
const Products = React.lazy(() => import('./pages/products/Products'));
const Settings = React.lazy(() => import('./pages/settings/Settings'));
const CompanySettings = React.lazy(() => import('./pages/settings/CompanySettings'));
const Users = React.lazy(() => import('./pages/Users'));
const Reports = React.lazy(() => import('./pages/reports/Reports'));
const OrderReports = React.lazy(() => import('./pages/reports/OrderReports'));
const CustomerReports = React.lazy(() => import('./pages/reports/CustomerReports'));
const ProductReports = React.lazy(() => import('./pages/reports/ProductReports'));
const PerformanceMetrics = React.lazy(() => import('./pages/reports/PerformanceMetrics'));
const SystemAdmin = React.lazy(() => import('./pages/admin/SystemAdmin'));
const AdminDashboard = React.lazy(() => import('./pages/admin/AdminDashboard'));
const UserManagement = React.lazy(() => import('./pages/admin/UserManagement'));
const UserPermissionManagement = React.lazy(() => import('./pages/admin/UserPermissionManagement'));
const BranchManagement = React.lazy(() => import('./pages/admin/BranchManagement'));
const PaymentManagement = React.lazy(() => import('./pages/admin/PaymentManagement'));
const AdminPlaceholder = React.lazy(() => import('./pages/admin/AdminPlaceholder'));
const AddCustomer = React.lazy(() => import('./pages/customers/AddCustomer'));
const OrderDetails = React.lazy(() => import('./pages/orders/OrderDetails'));
const SecuritySettings = React.lazy(() => import('./pages/admin/SecuritySettings'));
const PasswordSecuritySettings = React.lazy(() => import('./pages/admin/PasswordSecuritySettings'));
const SecurityMonitoringSettings = React.lazy(() => import('./pages/admin/SecurityMonitoringSettings'));
const BackupManagement = React.lazy(() => import('./pages/admin/BackupManagement'));
const RoleManagement = React.lazy(() => import('./pages/admin/RoleManagement'));
const TrainingDemo = React.lazy(() => import('./pages/TrainingDemo'));

function RequireAuth() {
  const location = useLocation();
  const isAuthenticated = authService.isAuthenticated();

  if (!isAuthenticated) {
    const redirectPath = `${location.pathname}${location.search}`;
    return <Navigate to={getLoginUrlWithRedirect(redirectPath)} replace />;
  }

  return <Outlet />;
}

function App() {
  return (
    <Provider store={store}>
      <ThemeProvider theme={theme}>
        <NotificationProvider>
        <Router>
          <React.Suspense fallback={<div>Loading...</div>}>
            <Routes>
              {/* Auth routes */}
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/forgot-password" element={<RequestPasswordReset />} />
              
              {/* Protected routes */}
              <Route element={<RequireAuth />}>
              <Route path="/training-demo" element={<TrainingDemo />} />
              <Route path="/" element={<MainLayout />}>
                <Route index element={<Navigate to="/dashboard" replace />} />
                <Route path="dashboard" element={<Dashboard />} />
                <Route path="orders" element={<Orders />} />
                <Route path="orders/new" element={<NewOrder />} />
                <Route path="orders/:orderId" element={<OrderDetails />} />
                <Route path="customers" element={<Customers />} />
                <Route path="customers/new" element={<AddCustomer />} />
                <Route path="products" element={<Products />} />
                <Route path="settings" element={<Settings />} />
                  <Route path="settings/sms" element={<SMSSettings />} />
                  <Route path="settings/notifications" element={<NotificationSettings />} />
                  <Route path="settings/company" element={<CompanySettings />} />
                  <Route path="users" element={<Users />} />
                  <Route path="reports" element={<Reports />} />
                  <Route path="reports/orders" element={<OrderReports />} />
                  <Route path="reports/customers" element={<CustomerReports />} />
                  <Route path="reports/products" element={<ProductReports />} />
                  <Route path="reports/performance" element={<PerformanceMetrics />} />
                  <Route path="admin" element={<SystemAdmin />} />
                  <Route path="admin/dashboard" element={<AdminDashboard />} />
                  <Route path="admin/users" element={<UserManagement />} />
                  <Route path="admin/users/permissions" element={<UserPermissionManagement />} />
                  <Route path="admin/payments" element={<PaymentManagement />} />
                  <Route path="admin/roles" element={<RoleManagement />} />
                  <Route path="admin/branches" element={<BranchManagement />} />
                  <Route path="admin/settings" element={<AdminPlaceholder title="System Settings" description="Configure system-wide settings, integrations, and preferences" color="info" />} />
                  <Route path="admin/security" element={<SecuritySettings />} />
                  <Route path="admin/password-security" element={<PasswordSecuritySettings />} />
                  <Route path="admin/security-monitoring" element={<SecurityMonitoringSettings />} />
                  <Route path="admin/backup" element={<BackupManagement />} />
                  <Route path="admin/backup-restore" element={<AdminPlaceholder title="Backup & Restore" description="Manage system backups, data export, and system recovery" color="secondary" />} />
                  <Route path="admin/notifications" element={<AdminPlaceholder title="Notification Settings" description="Configure email, SMS, and system notification settings" color="info" />} />
                  <Route path="admin/reports" element={<AdminPlaceholder title="Reports & Analytics" description="System reports, analytics, and performance monitoring" color="success" />} />
                  </Route>
              </Route>
              
              {/* Catch-all route for SPA - must be last */}
              <Route path="*" element={<Navigate to="/login" replace />} />
            </Routes>
          </React.Suspense>
        </Router>
        </NotificationProvider>
      </ThemeProvider>
    </Provider>
  );
}

export default App;
// Railway rebuild trigger
